/**
 * <bolder-widget> attributes — the one contract between the Distribute editor
 * and every way a customer installs a brain:
 *
 * - a saved widget's design (by widget id) is turned into this map by
 *   GET /api/widget/<id> for the web component and the hosted page,
 * - element attributes on <bolder-widget> can override it per page,
 * - and the Distribute preview renders the very same map,
 * all read back through `parseWidgetAttributes`.
 *
 * Everything that comes in here is untrusted (element attributes are written
 * by whoever controls the page), so every value is validated or dropped.
 */

import type { FloatingWrapperProps, WidgetLayout } from './components/FloatingWrapper';
import { getWidgetFont } from './theme/fonts';
import { resolveTheme } from './theme/presets';
import type { EmbedThemeConfig } from './theme/types';

export type { WidgetLayout };

export const WIDGET_OBSERVED_ATTRIBUTES = [
  // A saved Distribute widget: its design is read by id (see web-component.tsx).
  'widget-id',
  'embed-height',
  'theme',
  'title',
  'primary-color',
  'border-radius',
  'font-family',
  'graphql-url',
  'api-base-url',
  'app-base-url',
  'media-base-url',
  'asset-api-url',
  'path',
  'class',
  'owner-id',
  'owner-handle',
  'embed-mode',
  'guest-mode',
  'initial-message',
  'show-configure-button',
  'agent-icon',
  'force-login',
  'selected-brains',
  'advanced-config',
  'headless',
  'only-popup',
  'layout',
  'position',
  'panel-width',
  'panel-height',
  'floating-label',
  'greeting',
  'mobile-mode',
  'conversation-starters',
  'chat-style',
  'display-name-override',
  'header-subtitle',
  'display-image-override',
  'button-style',
];

export const PANEL_WIDTH = { min: 320, max: 600, default: 380 };
export const PANEL_HEIGHT = { min: 420, max: 900, default: 600 };

/**
 * A picture the widget may show: a media-library key (`media/abc.png`, resolved
 * against media-base-url) or an https URL. Anything else (other schemes,
 * protocol-relative, path traversal, quotes) is dropped.
 */
export function sanitizeWidgetImage(value: string | null | undefined): string {
  const ref = (value ?? '').trim();
  if (!ref || ref.length > 1024 || /["'<>\\\s]/.test(ref)) return '';
  if (/^https:\/\/[^/]/i.test(ref)) return ref;
  if (/^[a-z][a-z0-9+.-]*:/i.test(ref) || ref.startsWith('//') || ref.includes('..')) return '';
  return ref;
}

/** Full URL for a widget picture: https URLs as-is, media keys under the media base URL. */
export function widgetImageUrl(ref: string | null | undefined, mediaBaseUrl?: string): string {
  const safe = sanitizeWidgetImage(ref);
  if (!safe) return '';
  if (/^https:\/\//i.test(safe)) return safe;
  return mediaBaseUrl ? `${mediaBaseUrl}${safe}` : '';
}

const RADII = ['none', 'sm', 'md', 'lg', 'full'] as const;
type Radius = (typeof RADII)[number];
const AGENT_ICONS = ['robot', 'sparkle', 'brain', 'chat', 'message'] as const;
type AgentIcon = (typeof AGENT_ICONS)[number];

const NAMED_COLORS = [
  'red',
  'blue',
  'green',
  'yellow',
  'orange',
  'purple',
  'pink',
  'black',
  'white',
  'gray',
  'grey',
  'cyan',
  'magenta',
  'lime',
  'navy',
  'teal',
  'aqua',
  'maroon',
  'olive',
  'silver',
  'fuchsia',
];

export function isValidWidgetColor(color: string | null | undefined): color is string {
  if (!color) return false;
  return /^#([A-Fa-f0-9]{6}|[A-Fa-f0-9]{3})$/.test(color) || NAMED_COLORS.includes(color.toLowerCase());
}

/** http(s) URLs only, no private-network hosts (localhost is allowed for development). */
export function isValidWidgetEndpoint(url: string | null | undefined): url is string {
  if (!url) return false;
  try {
    const parsed = new URL(url);
    if (!['http:', 'https:'].includes(parsed.protocol)) return false;
    const hostname = parsed.hostname.toLowerCase();
    if (hostname === 'localhost' || hostname === '127.0.0.1') return true;

    const privatePatterns = [
      /^127\./,
      /^10\./,
      /^172\.(1[6-9]|2[0-9]|3[01])\./,
      /^192\.168\./,
      /^0\./,
      /^169\.254\./,
      /\.local$/i,
    ];
    if (privatePatterns.some((p) => p.test(hostname))) return false;

    // An https page must not talk to plain-http endpoints. A srcdoc iframe has
    // no hostname, so the check is skipped there.
    if (
      typeof window !== 'undefined' &&
      window.location.hostname &&
      window.location.protocol === 'https:' &&
      parsed.protocol !== 'https:'
    ) {
      return false;
    }
    return true;
  } catch {
    return false;
  }
}

/** Whole-number pixel size clamped to [min, max]; undefined when absent or not a number. */
export function clampPixels(value: string | null | undefined, min: number, max: number): number | undefined {
  if (!value) return undefined;
  const num = parseInt(value, 10);
  if (Number.isNaN(num)) return undefined;
  return Math.max(min, Math.min(max, num));
}

export function prefersDarkScheme(): boolean {
  try {
    return typeof window !== 'undefined' && window.matchMedia('(prefers-color-scheme: dark)').matches;
  } catch {
    return false;
  }
}

function isTrueAttr(value: string | null): boolean {
  return value === 'true' || value === '';
}

function parseJson(value: string | null): unknown {
  if (!value) return undefined;
  try {
    return JSON.parse(value);
  } catch {
    return undefined;
  }
}

export type AttributeReader = (name: string) => string | null;

/** The rendered props for a set of <bolder-widget> attributes (callbacks are added by the caller). */
export function parseWidgetAttributes(get: AttributeReader): FloatingWrapperProps {
  // --- Theme: a preset name, light/dark/auto, plus optional brand overrides.
  const themeAttr = get('theme');
  const primaryColorAttr = get('primary-color');
  const primaryColor = isValidWidgetColor(primaryColorAttr) ? primaryColorAttr : undefined;
  const radiusAttr = get('border-radius');
  const borderRadius = RADII.includes(radiusAttr as Radius) ? (radiusAttr as Radius) : undefined;
  const font = getWidgetFont(get('font-family'));
  const isAuto = themeAttr === 'auto';

  let theme: string | EmbedThemeConfig = themeAttr || 'light';
  if (isAuto || primaryColor || borderRadius || font) {
    const base: EmbedThemeConfig = isAuto
      ? { mode: prefersDarkScheme() ? 'dark' : 'light' }
      : resolveTheme(themeAttr || 'light');
    theme = {
      ...base,
      ...(primaryColor ? { primaryColor } : {}),
      ...(borderRadius ? { borderRadius } : {}),
      ...(font ? { fontFamily: font.stack } : {}),
    };
  }

  // --- Brains
  let selectedBrains: Array<{ id: string; name: string; displayImage?: string; description?: string }> = [];
  const brainsParsed = parseJson(get('selected-brains'));
  if (Array.isArray(brainsParsed)) {
    selectedBrains = brainsParsed.filter(
      (b) => b && typeof b.id === 'string' && typeof b.name === 'string' && /^[a-zA-Z0-9-]+$/.test(b.id),
    );
  }

  const advancedParsed = parseJson(get('advanced-config'));
  const advancedConfig =
    advancedParsed && typeof advancedParsed === 'object' && !Array.isArray(advancedParsed)
      ? (advancedParsed as FloatingWrapperProps['advancedConfig'])
      : undefined;

  let conversationStarters: string[] | undefined;
  const startersParsed = parseJson(get('conversation-starters'));
  if (Array.isArray(startersParsed)) {
    const cleaned = startersParsed.filter((s): s is string => typeof s === 'string' && s.trim().length > 0);
    if (cleaned.length > 0) conversationStarters = cleaned;
  }

  // --- Layout. `only-popup` predates `layout` and still means "no launcher".
  const layoutAttr = get('layout');
  const layout: WidgetLayout = layoutAttr === 'inline' || layoutAttr === 'fullpage' ? layoutAttr : 'floating';

  const agentIconAttr = get('agent-icon');
  const chatStyleAttr = get('chat-style');
  const endpoint = (name: string) => {
    const value = get(name);
    return isValidWidgetEndpoint(value) ? value : undefined;
  };

  return {
    layout,
    onlyPopup: isTrueAttr(get('only-popup')),
    position: get('position') === 'bottom-left' ? 'bottom-left' : 'bottom-right',
    panelWidth: clampPixels(get('panel-width'), PANEL_WIDTH.min, PANEL_WIDTH.max),
    panelHeight: clampPixels(get('panel-height'), PANEL_HEIGHT.min, PANEL_HEIGHT.max),
    fontId: font?.id,
    useShadowDom: true,
    floatingLabel: get('floating-label') || undefined,
    greeting: get('greeting') || undefined,
    mobileMode: get('mobile-mode') === 'fullscreen' ? 'fullscreen' : 'sheet',
    conversationStarters,
    chatStyle: chatStyleAttr === 'minimal' || chatStyleAttr === 'cards' ? chatStyleAttr : 'bubbles',
    displayNameOverride: get('display-name-override') || undefined,
    headerSubtitle: get('header-subtitle') || undefined,
    displayImageOverride: sanitizeWidgetImage(get('display-image-override')) || undefined,
    buttonStyle: get('button-style') === 'icon' ? 'icon' : 'picture',
    theme: theme as FloatingWrapperProps['theme'],
    title: get('title') || undefined,
    primaryColor,
    graphqlUrl: endpoint('graphql-url'),
    apiBaseUrl: endpoint('api-base-url'),
    appBaseUrl: endpoint('app-base-url'),
    mediaBaseUrl: endpoint('media-base-url'),
    assetApiUrl: endpoint('asset-api-url'),
    path: get('path') || undefined,
    className: get('class') || undefined,
    id: get('owner-id') || undefined,
    ownerHandle: get('owner-handle') || undefined,
    guestMode: isTrueAttr(get('guest-mode')),
    initialMessage: get('initial-message') || undefined,
    // Defaults to true for external embeds; show-configure-button="false" turns it off.
    showConfigureButton: get('show-configure-button') !== 'false',
    agentIcon: AGENT_ICONS.includes(agentIconAttr as AgentIcon) ? (agentIconAttr as AgentIcon) : 'message',
    forceLogin: get('force-login') === 'true',
    selectedBrains: selectedBrains.length > 0 ? selectedBrains : undefined,
    advancedConfig,
    headless: isTrueAttr(get('headless')),
  };
}
